// InputComponent.js
/**
 * @category Components
 * @subcategory ui
 */
class InputComponent {
  /**
   * Constructor for InputComponent.
   * @param {Object} options - Configuration options for the input.
   * @param {string} options.label - Label text for the input.
   * @param {string} [options.value=""] - Initial value of the input.
   * @param {boolean} [options.readOnly=false] - Whether the input is read-only.
   * @param {string} [options.type="text"] - Input type (e.g., "text", "number").
   * @param {Function} [options.onChange] - Callback for the input change event.
   */
  constructor({
    label,
    value = "",
    readOnly = false,
    type = "text",
    onChange,
    className,
  }) {
    this.label = label;
    this.eventListeners = []; // Track event listeners for proper disposal
    this.type = type;
    this.onChange = onChange || (() => {}); // Default to a no-op if no handler is provided
    this.inputContainer = this.createInputContainer(
      label,
      value,
      readOnly,
      className
    );

    this.clearInputValue(false);
  }

  cleanup() {
    console.log(`✅ Cleaning up InputComponent: ${this.label}`);

    // Remove all tracked event listeners
    this.eventListeners.forEach(({ eventType, callback }) => {
        this.inputField.removeEventListener(eventType, callback);
    });

    // Remove focus event listener if enabled
    if (this.clearOnFocus) {
        this.inputField.removeEventListener("focus", this.clearOnFocus);
    }

    this.eventListeners = []; // Clear the event listener array

    // Remove the input field and container from the DOM
    if (this.inputContainer && this.inputContainer.parentNode) {
        this.inputContainer.parentNode.removeChild(this.inputContainer);
    }

    // Nullify object references for garbage collection
    this.inputField = null;
    this.inputContainer = null;
    this.clearOnFocus = null;
    this.onChange = null;

    console.log(`✅ InputComponent cleanup complete.`);
}

  /**
   * Creates the input container with label and input field.
   * @param {string} label - The label text for the input.
   * @param {string} value - Initial value for the input field.
   * @param {boolean} readOnly - Whether the input field is read-only.
   * @returns {HTMLElement} - The container element with the input and label.
   */
  createInputContainer(label, value, readOnly, className) {
    const container = document.createElement("div");
    container.classList.add("info-div");

    if (label) {
      const inputLabel = document.createElement("div");
      inputLabel.classList.add("label-div");
      inputLabel.textContent = `${label}: `;
      container.appendChild(inputLabel);
    }

    this.inputField = document.createElement("input");
    this.inputField.classList.add("moreInfo-inputs");

    // Apply the additional class passed from ContextMenuModalUI
    if (className) {
      this.inputField.classList.add(className);
    }

    this.inputField.type = this.type;
    this.inputField.value = value;
    this.inputField.readOnly = readOnly;

    if (!readOnly) {
      this.addEventListener("input", this.handleInput.bind(this));
    }

    container.appendChild(this.inputField);
    return container;
  }

  /**
   * Handles input event, allowing only valid numbers if the type is "number".
   * @param {Event} e - The input event object.
   */
  handleInput(e) {
    if (this.type === "number") {
      e.target.value = e.target.value.replace(/[^0-9.]/g, "");
    }
    this.onChange(e);
  }

  /**
   * Adds an event listener and tracks it for removal on dispose.
   * @param {string} eventType - The type of event (e.g., "input").
   * @param {Function} callback - The event handler function.
   */
  addEventListener(eventType, callback) {
    this.inputField.addEventListener(eventType, callback);
    this.eventListeners.push({ eventType, callback });
  }

  clearInputValue(isEnabled) {
    this.inputField.removeEventListener("focus", this.clearOnFocus);

    if (isEnabled) {
      this.clearOnFocus = (e) => {
        if (!this.inputField.readOnly && this.type === "number")
          e.target.value = "";
      };
      this.inputField.addEventListener("focus", this.clearOnFocus);
    }
  }
  /**
   * Disposes of the component by removing event listeners and cleaning up the DOM.
   */
  dispose() {
    this.eventListeners.forEach(({ eventType, callback }) => {
      this.inputField.removeEventListener(eventType, callback);
    });
    this.eventListeners = []; // Clear tracked listeners
    this.inputContainer.remove(); // Remove the container from the DOM
  }
}

export default InputComponent;
